import {activeSelectionRange, caretFromPoint} from 'lib/domSelection';

import {resolveAnchor} from './annotationAnchors';
import {HIGHLIGHT_NAME_BY_COLOR, UNDERLINE_HIGHLIGHT_NAME} from '../constants';
import type {ResolvedMark} from './annotationMarkElements';
import type {TextProjection} from './annotationTextProjection';
import {
  ANNOTATION_REGION_ATTRIBUTE,
  domRangeToNormalized,
  isNodeAnnotatable,
  normalizedRangeToDomRange,
  normalizedStartAt
} from './annotationTextProjection';
import type {Annotation, AnnotationAnchor, AnnotationRange} from '../types';

function highlightNameForAnnotation(annotation: Annotation): string {
  return annotation.type === 'underline'
    ? UNDERLINE_HIGHLIGHT_NAME
    : HIGHLIGHT_NAME_BY_COLOR[annotation.color];
}

/**
 * The id of the annotation covering a viewport point — painted highlights aren't
 * elements, so hit-test via normalized offsets. Only currently-resolving
 * annotations can be hit (a dropped mark never opens a popover); on overlap the
 * most recently added one wins.
 */
export function findAnnotationAtPoint(
  container: Element,
  projection: TextProjection,
  x: number,
  y: number,
  annotations: Annotation[]
): string | null {
  const caret = caretFromPoint(x, y);
  if (!caret || !isNodeAnnotatable(caret.node, container)) {
    return null;
  }
  const offset = normalizedStartAt(projection, caret.node, caret.offset);
  for (let i = annotations.length - 1; i >= 0; i--) {
    const annotation = annotations[i];
    const resolved = resolveAnchor(annotation.anchor, projection);
    if (resolved === null || offset < resolved.start || offset >= resolved.end) {
      continue;
    }
    return annotation.id;
  }
  return null;
}

/**
 * An editor anchor at a bare point: the popover hangs below the click, not the
 * mark, whose box can run far past where the student released.
 */
export function annotationAnchorAtPoint(x: number, y: number, id: string): AnnotationAnchor {
  return {rect: new DOMRect(x, y), id};
}

/** An editor anchor hanging below `element`, from its box's bottom-left corner. */
export function annotationAnchorBelow(element: Element, id: string): AnnotationAnchor {
  const box = element.getBoundingClientRect();
  return annotationAnchorAtPoint(box.left, box.bottom, id);
}

/**
 * The viewport point a keyboard capture hangs its editor from: the bottom-left of the
 * caret ending the selection, as the mouseup point is for a drag.
 */
export function selectionCaretPoint(): {x: number; y: number} | null {
  const selection = window.getSelection();
  const range = activeSelectionRange();
  if (!range || !selection?.focusNode) {
    return null;
  }
  const caret = document.createRange();
  caret.setStart(selection.focusNode, selection.focusOffset);
  const caretBox = caret.getBoundingClientRect();
  if (caretBox.height > 0) {
    return {x: caretBox.left, y: caretBox.bottom};
  }
  // A caret at an element boundary (a triple-click's end) has no box: take the last line
  // of the selection, never its whole bounding box, which can run past the viewport.
  const lines = range.getClientRects();
  const box = lines[lines.length - 1] ?? range.getBoundingClientRect();
  return {x: box.left, y: box.bottom};
}

/**
 * Converts the current window selection to normalized offsets. Null when the
 * selection is missing, collapsed, or covers nothing annotatable.
 */
export function getSelectionNormalizedRange(projection: TextProjection): AnnotationRange | null {
  const range = activeSelectionRange();
  return range && domRangeToNormalized(projection, range);
}

/**
 * True when the range reaches into a region nested in `container` — a passage inside
 * a prompt. That text is the nested region's to mark, and one anchor cannot span both.
 */
export function selectionCoversNestedRegion(range: Range, container: Element): boolean {
  return Array.from(container.querySelectorAll(`[${ANNOTATION_REGION_ATTRIBUTE}]`)).some(
    (region) => range.intersectsNode(region) && (region.textContent ?? '').trim() !== ''
  );
}

/**
 * Resolves each anchor (validate-or-drop) and buckets the Ranges by highlight
 * name; dropped anchors are skipped, never approximated. Painting the result
 * is the coordinator's job (annotationPaintCoordinator.ts).
 */
export function resolveAnnotationRanges(
  projection: TextProjection,
  annotations: Annotation[]
): Map<string, Range[]> {
  const rangesByName = new Map<string, Range[]>();
  annotations.forEach((annotation) => {
    const resolved = resolveAnchor(annotation.anchor, projection);
    const range = resolved && normalizedRangeToDomRange(projection, resolved);
    if (!range) {
      return;
    }
    const name = highlightNameForAnnotation(annotation);
    const ranges = rangesByName.get(name) ?? [];
    ranges.push(range);
    rangesByName.set(name, ranges);
  });
  return rangesByName;
}

/** Each text annotation that still resolves, with its normalized range; dropped anchors are skipped. */
export const resolveMarks = (
  projection: TextProjection,
  annotations: Annotation[]
): ResolvedMark[] =>
  annotations.flatMap((annotation) => {
    const range = resolveAnchor(annotation.anchor, projection);
    return range ? [{annotation, range, text: projection.text.slice(range.start, range.end)}] : [];
  });
